/**
 * MobilePageHeader
 *
 * Standardised mobile page header for React pages (GRAIN-2350).
 * Renders below md breakpoint:
 *   - Hamburger nav trigger (opens mobile sidebar drawer)
 *   - Page title (Kapra) + optional Counter
 *   - Optional search toggle that reveals a full-width PageHeaderSearchBar
 *     in a row below the title (above the header divider)
 *
 * Desktop header (total label + centered search) remains in the sibling
 * md:flex block in the consuming Page.Header.
 *
 * Callers provide aria labels and placeholders via pure i18n (no fallbacks here).
 * Used by list pages to ensure consistent mobile chrome + nav access across
 * all React pages.
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Counter } from '@/components/ui/counter';
import { PageHeaderSearchBar } from './PageHeader';
import { CloseIcon, SearchIcon } from '@components/icons';
import { MobileIconButton } from './MobileButtons';
import { openMobileSidebar } from '@components/MobileSidebar/mobileSidebarEvents';

export interface MobilePageHeaderProps {
  /** Page or section title shown in the mobile header bar. */
  title: string;
  /** Count to show next to the title (string for '…' loading state etc). */
  count?: string | number;
  /** Rendered beside the title, for a badge the desktop header shows inline. */
  titleBadge?: React.ReactNode;
  /** Current search value (controlled). Only relevant if onSearchChange provided. */
  searchValue?: string;
  /** Called when user types in (or clears) search. When provided, search affordance is shown. */
  onSearchChange?: (value: string) => void;
  /** Placeholder for the search input when expanded. */
  searchPlaceholder?: string;
  /** ARIA label for the search input. */
  searchAriaLabel?: string;
  /** ARIA label for the open navigation (hamburger) button. */
  openNavigationAriaLabel?: string;
  /** ARIA label for the open search button. */
  openSearchAriaLabel?: string;
  /** ARIA label for the close search button. */
  closeSearchAriaLabel?: string;
  /** Override for opening nav (defaults to the app-wide openMobileSidebar). */
  onOpenNavigation?: () => void;
  /** Optional action rendered before the search toggle (e.g. a filter button). Hidden while search is open. */
  actions?: React.ReactNode;
  className?: string;
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg
      width="18"
      height="14"
      viewBox="0 0 18 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <rect width="18" height="2" rx="1" fill="currentColor" />
      <rect y="6" width="18" height="2" rx="1" fill="currentColor" />
      <rect y="12" width="18" height="2" rx="1" fill="currentColor" />
    </svg>
  );
}

export function MobilePageHeader({
  title,
  count,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  openNavigationAriaLabel,
  openSearchAriaLabel,
  closeSearchAriaLabel,
  onOpenNavigation = openMobileSidebar,
  actions,
  titleBadge,
  className,
}: MobilePageHeaderProps) {
  const [searchOpen, setSearchOpen] = useState(false);

  const hasSearch = Boolean(onSearchChange);

  const handleSearchToggle = () => {
    if (searchOpen && onSearchChange) {
      onSearchChange('');
    }
    setSearchOpen(!searchOpen);
  };

  return (
    <div className={cn('flex w-full flex-col gap-4', className)}>
      <div className="flex w-full items-center gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-6">
          <button
            type="button"
            aria-label={openNavigationAriaLabel}
            className="shrink-0 touch-manipulation text-off-black hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black/30"
            onClick={onOpenNavigation}
          >
            <MenuIcon />
          </button>
          <div className="flex min-w-0 items-center gap-4">
            <h1 className="truncate font-kapra-expanded text-2xl leading-normal font-black uppercase text-off-black text-balance">
              {title}
            </h1>
            {count !== undefined && <Counter value={count} />}
            {titleBadge}
          </div>
        </div>
        {actions}
        {hasSearch && (
          <MobileIconButton
            aria-label={searchOpen ? closeSearchAriaLabel : openSearchAriaLabel}
            onClick={handleSearchToggle}
          >
            {searchOpen ? <CloseIcon className="size-4" /> : <SearchIcon className="size-5" />}
          </MobileIconButton>
        )}
      </div>
      {/* Search expands into its own row below the title, above the header
          divider — shared by every list page (orders, events, …). */}
      {searchOpen && hasSearch && (
        <PageHeaderSearchBar
          value={searchValue}
          onChange={onSearchChange!}
          placeholder={searchPlaceholder}
          ariaLabel={searchAriaLabel}
        />
      )}
    </div>
  );
}
